import { useEffect } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import constants from '@/constants'
import useI18n from '@/hooks/useI18n'
import { ArrowStandIcon } from '@/assets/icons'
import ButtonIcon from '@/components/ui/ButtonIcon/ButtonIcon'
import {
  fetchCategories,
  selectCategoriesByParentId,
  selectCategoryById,
  selectCategoryStatusById,
} from '@/store/categories'
import { cryptoPriceFacetRows, nextCryptoPriceSelection } from './priceCategories'
import '@/features/categories-side-menu/CategoriesSideMenuNavigation.scss'

const Count = ({ count }) => {
  if (count == null) {
    return <span className="side-menu__item-count side-menu__item-count--pending" aria-hidden="true" />
  }
  return <span className="side-menu__item-count">{count}</span>
}

const FacetButton = ({ label, count, active, onClick }) => (
  <li className="side-menu__item">
    <div className="side-menu__item-row">
      <button
        type="button"
        className={`side-menu__item-link side-menu__item-link--button ${
          active ? 'side-menu__item-link--active' : ''
        }`}
        aria-pressed={active}
        onClick={onClick}
      >
        <span className="side-menu__item-title">{label}</span>
        <Count count={count} />
      </button>
    </div>
  </li>
)

const CryptoPriceSideMenu = ({
  categoryId,
  activeMarketType,
  selectedWindow,
  selectedAsset,
  onSelect,
  onCollapse,
  showHeader = true,
  className = '',
}) => {
  const i18n = useI18n()
  const dispatch = useDispatch()
  const store = useStore()
  const children = useSelector(selectCategoriesByParentId(categoryId, activeMarketType))
  const categoryStatus = useSelector(selectCategoryStatusById(categoryId, activeMarketType))
  const parentCategory = useSelector(selectCategoryById(categoryId))
  const childKey = (children || []).map(child => child?.id).join(',')

  useEffect(() => {
    if (categoryId && categoryStatus === constants.IDLE) {
      dispatch(fetchCategories(categoryId, activeMarketType))
    }
  }, [categoryId, categoryStatus, activeMarketType, dispatch])

  useEffect(() => {
    if (!childKey) return
    const state = store.getState()
    childKey.split(',').forEach(id => {
      const status = state.categories.tree[activeMarketType]?.status?.[id]
      if (!status || status === constants.IDLE) {
        dispatch(fetchCategories(id, activeMarketType))
      }
    })
  }, [childKey, activeMarketType, dispatch, store])

  const { windows, assets } = cryptoPriceFacetRows(children)
  const allActive = !selectedWindow && !selectedAsset
  const isLoading = categoryStatus === constants.LOADING && (!children || children.length === 0)

  return (
    <nav className={`side-menu ${className}`}>
      {showHeader && (
        <div className="side-menu__header">
          <span className="side-menu__title">{i18n('filters.categories')}</span>
          {onCollapse && (
            <ButtonIcon
              size="medium"
              highlightOnHover
              cursorPointer
              handleOnClick={onCollapse}
              ariaLabel={i18n('filters.toggle_categories')}
            >
              <ArrowStandIcon width="16" height="16" style={{ transform: 'scaleX(-1)' }} />
            </ButtonIcon>
          )}
        </div>
      )}
      {isLoading ? (
        <div className="side-menu__skeleton">
          {[75, 60, 85, 70].map((width, index) => (
            <div key={index} className="side-menu__skeleton-item">
              <div className="side-menu__skeleton-text" style={{ width: `${width}%` }} />
              <div className="side-menu__skeleton-badge" />
            </div>
          ))}
        </div>
      ) : (
        <ul className="side-menu__list">
          <FacetButton
            label={i18n('markets.category_all')}
            count={parentCategory?.open_events_count}
            active={allActive}
            onClick={() => onSelect?.({ window: null, asset: null })}
          />
          {windows.map(row => (
            <FacetButton
              key={row.id}
              label={i18n(row.labelKey)}
              count={row.count}
              active={selectedWindow === row.id}
              onClick={() =>
                onSelect?.(
                  nextCryptoPriceSelection(
                    { window: selectedWindow, asset: selectedAsset },
                    'window',
                    row.id
                  )
                )
              }
            />
          ))}
          {assets.length > 0 && <li className="side-menu__section" aria-hidden="true" />}
          {assets.map(row => (
            <FacetButton
              key={row.id}
              label={row.label}
              count={row.count}
              active={selectedAsset === row.id}
              onClick={() =>
                onSelect?.(
                  nextCryptoPriceSelection(
                    { window: selectedWindow, asset: selectedAsset },
                    'asset',
                    row.id
                  )
                )
              }
            />
          ))}
        </ul>
      )}
    </nav>
  )
}

export default CryptoPriceSideMenu
